import { STORE_CURRENCY } from "../constants/wordpress";

export function stripHtml(html = "") {
  if (!html) return "";
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function formatPrice(price, currency = STORE_CURRENCY) {
  if (price === null || price === undefined || price === "") return null;
  const amount = Number.parseFloat(price);
  if (Number.isNaN(amount)) return null;

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(amount);
  } catch {
    return `${price} ${currency}`;
  }
}

export function getDisplayCategories(categories = []) {
  return categories.filter(
    (category) => category.slug !== "uncategorized" && category.name,
  );
}

function getMetaValue(metaData = [], key) {
  const entry = metaData.find((item) => item.key === key);
  const value = entry?.value;
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function buildProductTranslations(raw) {
  const ar = {
    name: raw.name || "",
    shortDescription: stripHtml(raw.short_description),
    description: raw.description || "",
  };

  const nameEn = getMetaValue(raw.meta_data, "product_name_en");
  const shortDescriptionEn = getMetaValue(raw.meta_data, "short_description_en");
  const descriptionEn = getMetaValue(raw.meta_data, "description_en");

  const en = {
    name: nameEn || ar.name,
    shortDescription: shortDescriptionEn
      ? stripHtml(shortDescriptionEn)
      : ar.shortDescription,
    description: descriptionEn || ar.description,
  };

  return { ar, en };
}

export function getProductLanguage(language) {
  return language?.startsWith("ar") ? "ar" : "en";
}

function buildCategoryTranslations(raw) {
  const nameEn = String(raw.name || "").trim();
  const nameAr = String(raw.category_name_ar || raw.name || "").trim();

  return {
    ar: { name: nameAr || nameEn },
    en: { name: nameEn || nameAr },
  };
}

export function localizeCategory(category, language) {
  if (!category) return null;

  const lang = getProductLanguage(language);
  const localizedName = category.translations?.[lang]?.name || category.name;

  return {
    ...category,
    name: localizedName,
  };
}

export function localizeProduct(product, language, categoryById) {
  if (!product) return null;

  const lang = getProductLanguage(language);
  const localized = product.translations?.[lang];
  const categories = (product.categories || []).map((cat) => {
    const full = categoryById?.get(cat.id) || cat;
    return localizeCategory(full, language);
  });

  if (!localized) {
    return {
      ...product,
      categories,
    };
  }

  return {
    ...product,
    name: localized.name || product.name,
    shortDescription: localized.shortDescription ?? product.shortDescription,
    description: localized.description ?? product.description,
    categories,
  };
}

export function mapWooProduct(raw) {
  if (!raw) return null;

  const image = raw.images?.[0]?.src || null;
  const gallery = (raw.images || []).map((img) => img.src).filter(Boolean);
  const categories = (raw.categories || []).map((cat) => ({
    id: cat.id,
    name: cat.name,
    slug: cat.slug,
  }));

  const price = raw.price || raw.sale_price || raw.regular_price || "";
  const inStock = raw.stock_status !== "outofstock";
  const translations = buildProductTranslations(raw);

  return {
    id: raw.id,
    slug: raw.slug,
    name: translations.ar.name,
    shortDescription: translations.ar.shortDescription,
    description: translations.ar.description,
    translations,
    price,
    regularPrice: raw.regular_price,
    salePrice: raw.sale_price,
    onSale: Boolean(raw.on_sale),
    formattedPrice: formatPrice(price),
    formattedRegularPrice: formatPrice(raw.regular_price),
    formattedSalePrice: formatPrice(raw.sale_price),
    currency: STORE_CURRENCY,
    image,
    gallery,
    categories,
    sku: raw.sku || "",
    permalink: raw.permalink,
    purchasable: raw.purchasable !== false,
    inStock,
    stockStatus: raw.stock_status || "instock",
    relatedIds: raw.related_ids || [],
  };
}

export function mapWooCategory(raw) {
  if (!raw) return null;

  const translations = buildCategoryTranslations(raw);

  return {
    id: raw.id,
    name: translations.ar.name,
    slug: raw.slug,
    count: raw.count,
    translations,
  };
}
